import {
  OktonAuthBootstrap,
  OktonLogin,
  OktonSelect,
} from '@okton/platform-ui';
import { AxiosError } from 'axios';
import React, { useEffect, useMemo, useState } from 'react';

import { ILoginOpcoesData, useAuth } from '../../hooks/auth';
import { useLoginOpcoes } from '../../hooks/useLoginOpcoes';

const OKTON_SSO_URL =
  process.env.REACT_APP_OKTON_SSO_URL || 'https://sso.okton.com.br';

const OKTON_SSO_CLIENT_ID = 'integra';
const OKTON_SSO_CALLBACK = 'https://integra.okton.com.br/sso/callback';

const bootstrap: OktonAuthBootstrap = {
  system: {
    id: 'integra',
    name: 'Integra',
    description: 'Integrações Okton',
    url: 'https://integra.okton.com.br',
  },
  environment: process.env.NODE_ENV || 'production',
  auth: {
    mode: 'okton-sso',
    serverUrl: OKTON_SSO_URL,
    clientId: OKTON_SSO_CLIENT_ID,
    pkce: false,
    redirectUri: OKTON_SSO_CALLBACK,
    postLoginUrl: '/dashboard',
    postLogoutUrl: '/login',
    scopes: [],
    autoLogin: true,
  },
  companyContext: {
    mode: 'company_branch',
  },
  features: {
    forgotPassword: true,
    createAccount: true,
    billing: false,
    profile: true,
    branchSwitching: false,
    notifications: false,
  },
};

function criarSsoState(): string {
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);

  return Array.from(bytes)
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('');
}

function tentarLoginSsoAutomatico(): void {
  const pularUmaVez = sessionStorage.getItem('okton_sso_skip_once') === '1';

  if (pularUmaVez) {
    sessionStorage.removeItem('okton_sso_skip_once');
    return;
  }

  const state = criarSsoState();
  sessionStorage.setItem('okton_sso_state', state);

  const params = new URLSearchParams({
    client_id: OKTON_SSO_CLIENT_ID,
    redirect_uri: OKTON_SSO_CALLBACK,
    state,
  });

  window.location.assign(`${OKTON_SSO_URL}/authorize?${params.toString()}`);
}

function mensagemErro(error: unknown): string {
  const err = error as AxiosError<{ message?: string }>;
  return (
    err.response?.data?.message ||
    (error instanceof Error ? error.message : '') ||
    'Endereço de e-mail e/ou senha incorreta.'
  );
}

const SignIn: React.FC = () => {
  const { signIn } = useAuth();
  const { buscarOpcoesLogin, isBuscandoOpcoes } = useLoginOpcoes();

  const [opcoesLogin, setOpcoesLogin] = useState<ILoginOpcoesData | null>(null);
  const [empresaSelecionada, setEmpresaSelecionada] = useState('');
  const [filialSelecionada, setFilialSelecionada] = useState('');
  const [isEscolhendoFilial, setIsEscolhendoFilial] = useState(false);
  const [isConfirmandoFilial, setIsConfirmandoFilial] = useState(false);

  useEffect(() => {
    tentarLoginSsoAutomatico();
  }, []);

  const empresaAtual = useMemo(
    () =>
      opcoesLogin?.empresas.find(
        empresa => String(empresa.id) === empresaSelecionada,
      ),
    [opcoesLogin, empresaSelecionada],
  );

  const filiaisDisponiveis = empresaAtual?.filiais || [];

  const login = async (username?: string, password?: string): Promise<void> => {
    const email = username?.trim() || '';
    const senha = password || '';

    if (!email || !senha) {
      throw new Error('Informe e-mail e senha.');
    }

    if (!isEscolhendoFilial) {
      try {
        const opcoes = await buscarOpcoesLogin({ email, password: senha });

        const empresaPadrao =
          opcoes.empresa_padrao !== undefined
            ? String(opcoes.empresa_padrao)
            : String(opcoes.empresas?.[0]?.id || '');

        const empresaInicial =
          opcoes.empresas.find(
            empresa => String(empresa.id) === empresaPadrao,
          ) || opcoes.empresas[0];

        if (!empresaInicial) {
          throw new Error('Nenhuma empresa disponível para este usuário.');
        }

        const filialPadraoValida =
          opcoes.filial_padrao !== undefined &&
          empresaInicial.filiais.some(
            filial => String(filial.id) === String(opcoes.filial_padrao),
          )
            ? String(opcoes.filial_padrao)
            : '';

        const filialInicial =
          filialPadraoValida || String(empresaInicial.filiais?.[0]?.id || '');

        if (!filialInicial) {
          throw new Error('Nenhuma filial disponível para este usuário.');
        }

        setOpcoesLogin(opcoes);
        setEmpresaSelecionada(String(empresaInicial.id));
        setFilialSelecionada(filialInicial);
        setIsEscolhendoFilial(true);
        return;
      } catch (error) {
        throw new Error(mensagemErro(error));
      }
    }

    if (!empresaSelecionada || !filialSelecionada) {
      throw new Error('Selecione uma filial para continuar.');
    }

    try {
      setIsConfirmandoFilial(true);

      const result = await signIn({
        email,
        password: senha,
        empresa: empresaSelecionada,
        filial: filialSelecionada,
      });

      if (result.ssoBootstrapUrl) {
        window.location.assign(result.ssoBootstrapUrl);
      }
    } catch (error) {
      throw new Error(mensagemErro(error));
    } finally {
      setIsConfirmandoFilial(false);
    }
  };

  return (
    <OktonLogin
      bootstrap={bootstrap}
      login={login}
      isLoading={isBuscandoOpcoes || isConfirmandoFilial}
      redirectOnSuccess={false}
      submitLabel={isEscolhendoFilial ? 'Entrar' : 'Continuar'}
      loadingLabel={isEscolhendoFilial ? 'Entrando...' : 'Carregando...'}
      forgotPasswordUrl="/forgot"
      createAccountUrl="/create-account"
      extraContent={
        isEscolhendoFilial ? (
          <OktonSelect
            name="filial"
            label="Filial"
            value={filialSelecionada}
            onChange={event => setFilialSelecionada(event.target.value)}
          >
            {filiaisDisponiveis.map(filial => (
              <option key={filial.id} value={filial.id}>
                {filial.nome}
              </option>
            ))}
          </OktonSelect>
        ) : undefined
      }
    />
  );
};

export default SignIn;
